import { useId, useState } from 'react';
import { Icono } from './Icono';
import { TEXTOS_PIEZAS, type IdiomaPieza } from './textosPiezas';
import './piezas.css';

/**
 * RV Contador (componentes.md, «RV Contador»): personas, entradas, tablets. «−», el número y «+» (botones de 44). El número
 * se escribe (`role=spinbutton`): al salir o con Intro se ajusta a `min`/`max`; mientras se teclea fuera de rango sale
 * «Máximo N» / «Mínimo N» en rojo; ↑↓ suman y restan y Esc deshace. `tema="oscuro"` en el widget oscuro (G618). `idioma`
 * (G928, G996) pone en inglés «Quantity», «Decrease», «Increase» y «Minimum/Maximum N».
 */
interface Props {
    /** Lo que se cuenta («Personas»); también es la aria del grupo. Por defecto «Cantidad» (o «Quantity»). */
    etiqueta?: string;
    ayuda?: string;
    valor: number;
    min?: number;
    max?: number;
    onCambio: (n: number) => void;
    tema?: 'claro' | 'oscuro';
    bloqueado?: boolean;
    idioma?: IdiomaPieza;
}

export function Contador({ etiqueta, ayuda, valor, min = 1, max = 500, onCambio, tema = 'claro', bloqueado = false, idioma = 'es' }: Props) {
    const t = TEXTOS_PIEZAS[idioma];
    const nombre = etiqueta ?? t.cantidad;
    const [texto, setTexto] = useState<string | null>(null);
    const idNota = useId();
    const ajustar = (n: number) => Math.min(max, Math.max(min, n));
    const tecleado = texto === null || texto.trim() === '' ? null : Number(texto);
    const fuera = tecleado === null || Number.isNaN(tecleado) ? '' : tecleado > max ? t.maximo(max) : tecleado < min ? t.minimo(min) : '';
    const confirmar = () => {
        if (texto === null) {
            return;
        }
        const n = Number(texto);
        setTexto(null);
        if (texto.trim() !== '' && !Number.isNaN(n) && ajustar(Math.round(n)) !== valor) {
            onCambio(ajustar(Math.round(n)));
        }
    };
    return (
        <div className={`rv-sl-contador${tema === 'oscuro' ? ' rv-tema-oscuro' : ''}`} data-rv-tema={tema}>
            {(etiqueta !== undefined || ayuda !== undefined) && (
                <span className="rv-sl-contador__texto">
                    {etiqueta !== undefined && <strong>{etiqueta}</strong>}
                    {ayuda !== undefined && ayuda !== '' && <small>{ayuda}</small>}
                </span>
            )}
            <span className="rv-sl-contador__mando">
                <span className="rv-sl-contador__botones" role="group" aria-label={nombre}>
                    <button type="button" aria-label={t.restar} disabled={bloqueado || valor <= min} onClick={() => { setTexto(null); onCambio(ajustar(valor - 1)); }}>
                        <Icono nombre="minus" />
                    </button>
                    <input
                        className={`rv-sl-contador__numero${fuera !== '' ? ' rv-sl-contador__numero--mal' : ''}`}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        enterKeyHint="done"
                        autoComplete="off"
                        role="spinbutton"
                        aria-label={nombre}
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuenow={valor}
                        aria-invalid={fuera !== ''}
                        aria-describedby={fuera !== '' ? idNota : undefined}
                        disabled={bloqueado}
                        value={texto ?? String(valor)}
                        onFocus={(e) => { e.currentTarget.select(); }}
                        onChange={(e) => { setTexto(e.target.value.replace(/[^0-9]/g, '')); }}
                        onBlur={confirmar}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                confirmar();
                            } else if (e.key === 'Escape') {
                                setTexto(null);
                            } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                                setTexto(null);
                                onCambio(ajustar(valor + (e.key === 'ArrowUp' ? 1 : -1)));
                            }
                        }}
                    />
                    <button type="button" className="rv-sl-contador__mas" aria-label={t.sumar} disabled={bloqueado || valor >= max} onClick={() => { setTexto(null); onCambio(ajustar(valor + 1)); }}>
                        <Icono nombre="plus" />
                    </button>
                </span>
                {fuera !== '' && (
                    <span id={idNota} className="rv-sl-contador__fuera" role="alert">
                        {fuera}
                    </span>
                )}
            </span>
        </div>
    );
}
